import React from 'react';
import classNames from 'classnames';
import { translationKeys, l } from '@locale/setupI18n';
import {
  OpmFirstLastWaterfallDictionaryDto,
  FirstLastSummaryIndexDictionaryDto,
  FirstLastTranche,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { TrancheKey } from '@app/shared/models/contracts/enums/shared-enums';
import { getProceedsCapToolTipContent } from '@app/shared/helpers/proceeds-cap/proceeds-cap';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import { Tooltip, TooltipPlacement } from '@app/shared/components/tooltip/Tooltip';
import SvgInformation from '@app/shared/icons/Information';
import styles from '@app/modules/projects/outputs/opm-summary-waterfall/opm-summary-waterfall-erf.module.scss';
import { simulationSectionsDefinition } from '../../helper/FirstLastHelpers';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';

type TrancheValueKeys = Omit<FirstLastTranche, 'conditions' | 'ordinaryEquityMetrics'>;

interface TrancheLevelRowProps {
  label: translationKeys;
  valueKey: keyof TrancheValueKeys;
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto;
  selectedYear: string;
  firstSimulation: string;
  waterfallIterations: number;
  isPercentage?: boolean;
  instrumentKey: string;
  isBold?: boolean;
  upper?: boolean;
  currentTrancheIndex?: number;
}

export const TrancheLevelRow: React.FC<TrancheLevelRowProps> = ({
  label,
  valueKey,
  opmWaterfall,
  selectedYear,
  firstSimulation,
  waterfallIterations,
  isPercentage = false,
  instrumentKey,
  isBold = false,
  upper = false,
  currentTrancheIndex = 0,
}) => {
  const instrument = opmWaterfall[selectedYear].first[firstSimulation].instruments[instrumentKey];
  const instrumentHasTranches = instrument.numberOfPayoutLogicTranches > 0;
  const firstValues =
    !instrumentHasTranches &&
    Object.entries(opmWaterfall[selectedYear].first).map(([key, _]) => {
      return opmWaterfall[selectedYear].first[key].instruments[instrumentKey].tranches[
        TrancheKey.DefaultTrancheKey
      ];
    });

  const lastValues =
    !instrumentHasTranches &&
    Object.entries(opmWaterfall[selectedYear].last).map(([key, _]) => {
      return opmWaterfall[selectedYear].last[key].instruments[instrumentKey].tranches[
        TrancheKey.DefaultTrancheKey
      ];
    });

  const combinedValues = [...(firstValues || []), ...(lastValues || [])];

  const tooltipContent = getProceedsCapToolTipContent(
    valueKey,
    combinedValues.map((tranche) => tranche?.hasHitCap ?? false),
    combinedValues.map((tranche) => tranche?.hasReceivedEnhancedParticipation ?? false)
  );

  return (
    <tr>
      <td
        className={classNames(
          styles['border-right'],
          { [styles['bold-text']]: isBold },
          { [styles['tooltip-header']]: tooltipContent }
        )}
        data-testid={`${valueKey}-instrument-title`}>
        {l(label)}
        {tooltipContent && (
          <Tooltip content={tooltipContent} placement={TooltipPlacement.Right}>
            <Button
              appearance={ButtonAppearance.CLEAN}
              size={ButtonSize.FLUID}
              aria-label={tooltipContent}>
              <SvgInformation className={styles['warning-icon']} />
            </Button>
          </Tooltip>
        )}
      </td>
      {simulationSectionsDefinition.map((section, outerIndex) =>
        Object.entries(opmWaterfall[selectedYear][section]).map(([key, _], innerIndex) => {
          const firstTrancheName = Object.keys(
            (opmWaterfall[selectedYear][section] as FirstLastSummaryIndexDictionaryDto)[key]
              .instruments[instrumentKey].tranches
          )[currentTrancheIndex];
          const firstTranche = (
            opmWaterfall[selectedYear][section] as FirstLastSummaryIndexDictionaryDto
          )[key].instruments[instrumentKey].tranches[firstTrancheName];
          const value = firstTranche[valueKey];

          return (
            <td
              key={`${section}-${key}`}
              className={classNames({
                [styles['border-right']]:
                  outerIndex === 0 && innerIndex + 1 === waterfallIterations,
              })}
              data-testid={`${valueKey}-instrument-simulation-${key}`}>
              <CellValue
                className={
                  upper
                    ? value === false
                      ? styles['upper-text-value-false']
                      : styles['upper-text']
                    : ''
                }
                topLine={Boolean(valueKey === 'trancheDistribution' && firstTranche.hasHitCap)}
                plusSign={Boolean(
                  valueKey === 'trancheParticipationPc' &&
                    firstTranche.hasReceivedEnhancedParticipation
                )}
                value={
                  isPercentage && typeof value === 'number'
                    ? getCalculatedProjectValueInPercent(value)
                    : value
                }
                {...(isPercentage ? enteredPercentageViewRules : amountRules)}
              />
            </td>
          );
        })
      )}
    </tr>
  );
};
